/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useRef, useState, useEffect } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { useSpring, animated, to } from '@react-spring/web'
import { useGesture } from 'react-use-gesture'
import { Waypoint } from 'react-waypoint'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import './styles.scss'

// TODO Set the correct article ID here
const ARTICLE_ID = '604322740587'

export const isMobile = browserCheck.isMobile

const portraits = [
  {
    name: 'Mario Dantas',
    desc: '1ère génération',
    pict: 'mario_dantas.jpg',
    quote:
      '«Quand je suis ici, je suis le Portugais. Au Portugal, je suis le Suisse.»',
    intro: (
      <p>
        «Moi, j’avais de la famille en Suisse: deux cousins à Avenches, dont un
        avait épousé une Suissesse. C’est elle qui m’a trouvé mon premier job au
        restaurant de l’Hôtel de Ville. Elle a demandé à la patronne s’il y
        avait une place pour un petit jeune. J’ai commencé le lendemain comme
        garçon de cuisine!» Mario Dantas n’était qu’un ado quand il a débarqué
        dans ce qui était alors un petit village de la Broye. C’était en 1985.
        Il avait 17 ans. «Je venais du nord du Portugal. Là-bas, il n’y avait
        pas de travail, pas d’industrie. Ceux qui étaient à l’école avec moi
        sont tous partis, certains jusqu’au Canada.»
      </p>
    ),
    text: (
      <>
        <p>
          Quand il évoque son 1er anniversaire loin de chez lui, c’est la
          solitude d’une fête sans amis qui lui revient. Aujourd’hui, trente-six
          ans plus tard, les Portugais représentent 19,5% de la population
          d’Avenches. Mario Dantas se pince quand même pour y croire. Peu après
          son arrivée, il a fait partie des fondateurs du centre portugais
          d’Avenches, pour une raison simple: «Je me sentais isolé. D’une
          communauté à l’autre, les gens ne se mélangeaient pas et les Portugais
          n’intéressaient personne!»
        </p>
        <p>
          Au cœur du vieux village, le désormais quinquagénaire désigne un tout
          petit local en haut d’une volée d’escaliers. C’est là que la
          communauté a eu son premier lieu de réunion. «On a pris la place des
          Italiens quand ils sont partis!» sourit-il en l’observant depuis sa
          voiture. Mais l’espace n’a pas tardé à manquer, poussant le centre à
          se déplacer vers la zone industrielle dans les années 1990. C’est là
          qu’il a vécu sa grande époque. «En termes de surface, c’était le plus
          grand de Suisse et il n’y avait pas de mal à le remplir», se
          souvient-il en montrant l’ancienne devanture. Autrefois, on y voyait
          flotter les drapeaux portugais et suisse.
        </p>
        <h4>Venir pour rester</h4>
        <p>
          Ce temps-là paraît déjà loin, même si le centre a dû quitter les lieux
          il y a trois ou quatre ans à peine. Il s’est réinventé dans la même
          zone industrielle, à un jet de pierre, mais dans des locaux beaucoup
          plus petits. Mario Dantas se souvient qu’autrefois, cette petite
          baraque aux murs blancs abritait la cantine des ouvriers travaillant
          dans le coin. Il en sait quelque chose, il a été l’un d’entre eux
          pendant des années.
        </p>
        <p>
          Pour lui, le centre portugais était à l’origine surtout un refuge où
          on jouait aux cartes et où on palabrait. «Certains s’y sentaient bien
          juste parce qu’on y parlait portugais. C’était parfois dur de
          s’adapter ici, sans formation et sans maîtrise du français. Il y en a
          qui ont eu du mal à apprendre.» Aujourd’hui, beaucoup d’amis de son
          âge sont repartis au pays. Le centre accueille sa troisième génération
          de membres, moins nombreux, et sa vocation n’est plus tout à fait la
          même. «C’est davantage un bistrot maintenant et il attire d’autres
          gens, contrairement au temps où chacun était dans sa coquille.»
        </p>
        <p>
          Quand il parle des années 80, Mario Dantas décrit une époque où rien
          n’était simple pour les travailleurs qui tentaient leur chance en
          Suisse. Le régime saisonnier les obligeait à repartir tous les ans
          pendant trois mois. «Moi, je n’étais pas de ceux qui ne pensaient qu’à
          faire leur vie au Portugal.» Il acceptait les règles sans broncher,
          même celle qui imposait une visite médicale lors de chaque retour en
          Suisse. «Pour moi, c’était normal.»
        </p>
        <p>
          Après des débuts dans la restauration, il a trouvé un emploi dans une
          entreprise du coin, qui lui offrait un permis de travail plus stable.
          «Ils faisaient des éléments en béton préfabriqués. Vous voyez les murs
          antibruit qui longent l’autoroute Lausanne-Genève? J’ai travaillé pour
          ça pendant des années!»
        </p>
        <p>
          Et la tentation de revenir au pays? «Si! Au début des années 2000, le
          Portugal s’en sortait mieux économiquement et mon entreprise a
          commencé à péricliter. Là, j’y ai pensé.» Les plans ont changé à cause
          d’un accident l’obligeant à remonter la pente en Suisse, marié et
          jeune père de famille. Aujourd’hui, il dirige une petite entreprise
          d’import-export de cigares, toujours dans la zone industrielle
          avenchoise. «Quand je suis arrivé en Suisse, mon rêve n’était pas de
          travailler dans les préfabriqués en béton. Ça a pris des années, mais
          pour la première fois, j’aime mon job.»
        </p>
        <p>
          La fierté se lit aussi sur son visage quand il parle de ses deux
          grands enfants, désormais citoyens suisses. Lui ne peut pas en dire
          autant. «Avec ma femme, nous avons commencé des démarches de
          naturalisation, mais on nous a découragés», assure-t-il. La peur d’un
          refus a pris le dessus. «Quand je suis ici, je suis le Portugais. Au
          Portugal, je suis le Suisse», conclut-il, se laissant aller à la
          «saudade». «Je n’ai pas de pays.»
        </p>
      </>
    )
  },
  {
    name: 'Maria Pinto',
    desc: '1ère génération',
    pict: 'maria_pinto.jpg',
    quote:
      '«On n’avait pas d’autre choix que de commencer au bas de l’échelle.»',
    intro: (
      <p>
        Quand on lui demande son nom de famille, Maria part d’un large sourire
        et dicte patiemment: De Jesus Almeida Ribeiro Pinto. «Les gens
        pourraient se demander combien de personnes ça fait», plaisante-t-elle.
        Il y a deux ans, elle a eu l’occasion d’abréger ce long patronyme, si
        typique des Portugais. C’était au moment d’établir son passeport Suisse,
        en même temps que son mari et ses deux filles. «On a préféré ne rien
        changer. Parce que ces noms, c’est nous!»
      </p>
    ),
    text: (
      <>
        <p>
          Si nous avons contacté la famille Pinto, c’est pour une bonne raison.
          Dans leur petit village de Bière, au pied du Jura, 17,7% de la
          population est portugaise. Derrière Avenches, c’est la deuxième
          commune du canton où cette proportion est la plus élevée. Désormais
          binationaux, les Pinto ne figurent même plus dans cette statistique,
          qui recouvre uniquement les étrangers. Autant dire que l’esprit du
          Portugal plane un peu sur Bière, même si cela se remarque à peine.
          Antonio, le mari de Maria, n’en avait en effet aucune idée, pas plus
          que le syndic du village, qui nous a transmis son numéro.
        </p>
        <p>
          «C’est vrai que certaines entreprises de la région emploient
          énormément de Portugais, observe le Birolan. C’est parfois l’histoire
          de travailleurs qui font venir des gens de leur famille ou parmi leurs
          connaissances.» Responsable technique dans une entreprise du Nord
          vaudois, il y observe d’ailleurs le même phénomène sur son lieu de
          travail. «Mon premier jour, j’ai appris avec pas mal de surprise que
          sur 150 employés, environ 120 étaient Portugais.»
        </p>
        <h4>Investir dans une maison</h4>
        <p>
          En pensant à la présence lusitanienne dans son village, il analyse:
          «Les Portugais aiment investir dans leur maison, et ici les prix sont
          plus abordables qu’à Morges ou Rolle. Idem pour les loyers.» En effet,
          les Pinto ont choisi le cœur du village pour y acquérir leur
          appartement familial, à l’étage d’une jolie bâtisse ancienne. Mais la
          région est aussi celle d’Antonio depuis son enfance. Fils d’un ouvrier
          agricole saisonnier, il a rejoint son père avec le reste de sa famille
          quand il était encore tout jeune. C’était au début des années 80,
          lorsque les Portugais ont commencé à se tourner en nombre vers la
          Suisse.
        </p>
        <p>
          «Au début, c’était difficile de se faire accepter. Nous étions parmi
          les premiers à immigrer.» Antonio se souvient qu’à hauteur d’enfant,
          la langue était la clé. «Dès que j’ai su parler français, je n’ai plus
          eu de problème et à l’époque déjà, on avait de l’aide. À l’école, il
          n’y avait pas que des Portugais, mais aussi des Italiens et des
          Espagnols.»
        </p>
        <p>
          Se faire une place n’allait pas de soi, mais déjà, la Suisse se
          mettait au diapason d’une société multiculturelle. «On a eu beaucoup
          de chance de pouvoir se former», souligne Antonio. Il évoque la
          trajectoire de son père, qui après un accident de travail a pu
          rebondir en devenant aide mécanicien de précision dans l’industrie.
          «La Suisse nous a tout donné. C’est un pays où on peut commencer par
          un apprentissage et finir chef d’entreprise.»
        </p>
        <p>
          Maria, qui est arrivée au début des années 2000, en sait quelque
          chose, même si son histoire d’immigration n’est pas la même que celle
          de son mari. «J’ai commencé comme jeune fille au pair, puis j’ai
          travaillé comme femme de ménage. C’est vraiment le cliché portugais!
          Mais on n’avait pas d’autre choix que de débuter en bas de l’échelle.»
          L’histoire ne s’arrête pas là. Après un brevet d’intendance, elle a
          coiffé la casquette d’enseignante au Centre d’enseignement des métiers
          de l’économie familiale de Morges. Elle y occupe désormais le poste de
          doyenne.
        </p>
        <p>
          Aujourd’hui encore, la famille Pinto voit arriver des compatriotes qui
          ont parfois besoin d’un coup de main, pour écrire une lettre ou pour
          mieux comprendre comment fonctionne l’administration. «On a un petit
          rôle d’assistant social parfois! Il y a encore cette solidarité»,
          sourit Maria. Même à l’école, Inès, la cadette, a pris sous son aile
          des camarades fraîchement arrivés du Portugal. «On fait en sorte de
          nous asseoir à côté en classe et en une année ou deux, ils parlent
          français.»
        </p>
        <p>
          C’est ainsi que des liens se maintiennent entre Portugais d’ici,
          qu’ils soient nouveaux arrivés ou secundos. Cet esprit de communauté a
          toutefois évolué au fil des générations. Antonio et Maria se
          souviennent du temps où ils passaient leurs soirées dans les centres
          portugais. «C’est là que nous nous sommes rencontrés», sourit Antonio.
          «Ça nous réconfortait d’être entre nous», ajoute Maria. Mais cela fait
          longtemps qu’ils n’y ont plus remis les pieds. «Avec l’arrivée des
          enfants, les priorités changent», sourit Antonio.
        </p>
      </>
    )
  },
  {
    name: 'Luis Guedes',
    desc: '1ère génération',
    pict: 'luis_guedes.jpg',
    quote:
      '«Je ne veux pas que des Portugais votent pour moi, juste qu’ils votent.»',
    intro: (
      <p>
        Discrets dans la vie, les Portugais d’ici le sont aussi en politique.
        Dans les législatifs et des exécutifs, leur présence saute en effet
        rarement aux yeux. Mais est-ce plus qu’une impression? Difficile à dire
        dans le canton de Vaud, où l’on ne collecte aucune donnée sur la
        participation politique en fonction des nationalités. À Genève en
        revanche, une étude publiée en début d’année fait un constat: depuis
        l’introduction du droit de vote des étrangers au niveau communal en
        2005, deux tiers de la communauté portugaise ne s’en est jamais servi.
      </p>
    ),
    text: (
      <>
        <p>
          «Jusqu’aux dernières élections communales, mes parents n’avaient
          jamais voté. Ils l’ont fait pour la première fois pour moi», raconte
          Luis Guedes, 42 ans. Élu en mars dernier, il vient de prendre les
          habits de conseiller communal à Renens, au sein du PLR, et il ne cache
          pas sa fierté d’être un binational dans ces fonctions.
        </p>
        <p>
          Pendant la campagne, son parti, comme d’autres, a diffusé des
          incitations à voter en plusieurs langues, dont le portugais. «Pour
          moi, le but n’est pas d’activer une communauté, mais que la politique
          soit plus représentative. C’est ce que je disais à mon entourage avant
          les élections. Je ne veux pas que des Portugais votent pour moi, juste
          qu’ils votent.»
        </p>
        <p>
          S’engager, il y pensait depuis un certain temps. «La gestion de la
          ville ne me plaisait pas, mais il ne suffit pas de critiquer.» Luis
          Guedes n’est pas né à Renens, mais il n’y a aucun doute quand il en
          parle: la ville fait partie de lui. C’est là qu’il s’est construit,
          avec sa famille. «Quand il est arrivé en Suisse, mon père a commencé
          en faisant la plonge dans un hôtel du côté de Saint-Gall. Au bout du
          compte, il a tenu la brasserie de la Gare, ici à Renens pendant
          dix-huit ans.»
        </p>
        <p>
          Il évoque ce parcours avec émotion, regrettant que son père n’en soit
          pas plus fier. «Comme d’autres de cette génération, mes parents
          avaient le sentiment d’être tout en bas de l’échelle quand ils sont
          arrivés. Et malgré leur réussite, ce sentiment est quelque part
          resté.»
        </p>
        <p>
          Pour expliquer la discrétion de ces travailleurs de la première
          génération, une explication revient chez Luis Guedes comme chez
          d’autres. «Pour eux, c’était important de ne pas faire de vagues.
          Quand j’étais petit, j’entendais des récits de Portugais qui étaient
          renvoyés au pays parce qu’ils n’étaient pas en règle. Même si je
          n’étais pas dans cette situation, cela imprimait une certaine
          défiance.»
        </p>
        <h3>Gravir les marches</h3>
        <p>
          Contrairement à son père, Luis Guedes revendique sa trajectoire
          ascensionnelle. Arrivé en Suisse à 8 ans, il a appris la langue, s’est
          vite senti chez lui, mais n’a pas accroché à l’école. Ça ne l’a pas
          empêché d’atteindre un poste dans la gestion de fortune privée au sein
          d’un établissement de la place genevoise. «Je suis parti d’un CFC!
          C’est une grande force de la Suisse par rapport au Portugal. Là-bas,
          on me traitait de menteur quand je disais que j’étais apprenti dans
          une banque, à 16 ans et sans relations en haut lieu.»
        </p>
        <p>
          Comme d’autres enfants de saisonniers, Luis Guedes a vécu ses
          premières années séparé de ses parents, en attendant que s’ouvre le
          regroupement familial dans les années 80. «Je suis de ces secundos qui
          ont été élevés au pays par les grands-parents.» C’est ainsi qu’il
          explique le lien fort qu’il garde avec le Portugal, où il se doit de
          retourner au moins une fois par an. «À vingt ans, j’avoue que c’était
          dur de revenir des vacances. Mes parents devaient me convaincre de
          faire ma vie ici.»
        </p>
        <p>
          Pourtant, ses parents eux-mêmes ont longtemps vécu avec l’idée
          d’économiser et de regagner la terre natale. «Le piège qu’ils
          n’avaient pas vu venir, c’est l’arrivée de leurs enfants, puis celle
          des petits-enfants. Finalement, l’échéance du retour est toujours
          repoussée. Aujourd’hui, mon père est bientôt à la retraite et il pense
          toujours à repartir, mais force est de constater que le centre
          névralgique de la famille n’est plus au Portugal.»
        </p>
        <p>
          Luis Guedes ne cache pas qu’il a aussi ressenti cette tentation du
          retour, malgré toutes ces années passées en Suisse. C’était il y a
          quelques années, lorsque le secteur bancaire vivait des moments
          difficiles. «J’avais perdu mon travail et je venais de divorcer. C’est
          là que j’ai eu une offre au Portugal.» Il y a réfléchi sérieusement,
          avant de réaliser une chose pour la toute première fois: «Partir pour
          plus de six mois impliquait la perte de mon permis C, sans possibilité
          de revenir dans le pays où j’ai grandi. Tout d’un coup, je me suis
          senti comme si je pouvais être déchu de ma nationalité.» Il a décliné
          l’offre et a entrepris des démarches de naturalisation. Toujours à
          Renens, il est aujourd’hui marié et papa d’une petite fille.
        </p>
      </>
    )
  },
  {
    name: 'Nuno et Lucia Simoes',
    desc: '1ère génération',
    pict: 'nuno_lucia.jpg',
    quote:
      '«Nous avions aussi des propositions de travail en Australie, en Angleterre et en Norvège»',
    intro: (
      <p>
        Comme beaucoup de Portugais installés en Suisse, Nuno et Lucia Simoes
        ont passé des vacances bien méritées au pays cet été. Un must pour leur
        petite famille. «Normalement nous prenons l’avion, pour les enfants,
        mais là, avec les incertitudes liées aux restrictions sanitaires, nous
        avons fait le chemin en voiture», explique le quadra, papa de deux
        jeunes garçons. L’amour du Portugal, ce couple d’infirmiers le cultive
        de différentes manières, ici en Suisse. Entre autres, il est l’âme de la
        «Tuna Helvetica», un groupe de musiciens qui perpétue la tradition des
        «tunas», gardiennes des mélodies portugaises d’antan.
      </p>
    ),
    text: (
      <>
        <p>
          Ce pays, Lucia et Nuno l’ont pourtant quitté il y a une petite dizaine
          d’années, le temps de créer des racines ici et d’oublier la nostalgie
          qui fait rêver d’un retour. Arrivés avec la deuxième vague
          d’immigration portugaise en Suisse, celle des années 2000, ils ont
          fait le pas, comme d’autres travailleurs qualifiés. «C’était la crise
          en 2008, mais la situation de notre profession était déjà difficile.
          Les salaires étaient gelés et il y avait des carences de personnel et
          de matériel à cause des politiques publiques», se souvient Nuno. Il en
          était arrivé à cumuler trois emplois, avec toujours la peur d’en
          perdre un. «À Lisbonne, j’étais épuisé.»
        </p>
        <p>
          À l’époque, les infirmiers portugais, formés à l’université, étaient
          démarchés activement depuis l’étranger dans des forums professionnels.
          «On nous a proposé des postes en Australie, en Angleterre et en
          Norvège», égrène Nuno. Finalement, Lucia et lui répondent à
          l’invitation d’une société de recrutement lausannoise. «Nous sommes
          venus sur place à l’hiver 2008 pour nous faire une idée, et nous avons
          tout de suite eu des offres concrètes pour travailler à Montreux, à
          l’Hôpital Riviera Chablais», se souvient Nuno. Le choix du couple est
          donc fait, ce sera la Suisse, «pour sa proximité avec le Portugal et
          sa qualité de vie», explique Lucia.
        </p>
        <p>
          Un an après leur installation, les deux infirmiers rejoignent le CHUV,
          où Lucia travaille toujours, tandis que Nuno a pris un poste à la
          centrale des médecins de garde tout récemment. «Quand je suis arrivée,
          j’avais quelques notions de français, mais j’ai pu commencer à
          travailler très rapidement, grâce à un système de parrainage à
          l’hôpital», se souvient Lucia. L’apprentissage s’est fait en un
          éclair, grâce à son mari, qui parle depuis toujours un français
          parfait.
        </p>
        <p>
          C’est que Nuno a grandi à Paris, qui accueille aussi une très grande
          communauté portugaise. La nostalgie et le retour au pays, il en a déjà
          fait l’expérience: à 20 ans il a choisi Lisbonne pour y faire ses
          études. «Mes parents m’ont transmis un amour démesuré du Portugal,
          mais je suis sorti de cet angélisme après y avoir vécu. La première
          génération d’immigrants est attachée à un pays qui n’existe plus.»
        </p>
        <h4>S’intégrer sans oublier</h4>
        <p>
          Cette rupture, il l’observe aussi en Suisse. «Un exemple: les centres
          portugais ne nous attirent pas, car on n’y trouve pas le pays que nous
          avons laissé derrière nous. C’est comme si deux communautés
          portugaises s’étaient créées. Elles se rencontrent facilement, mais
          elles ne partagent pas forcément les mêmes intérêts.»
        </p>
        <p>
          Ce Portugal qu’ils ont quitté, Lucia et Nuno prévoyaient d’y repartir.
          Mais ça, c’était au début. «On se donnait cinq ans pour économiser
          afin d’acheter une maison là-bas. Finalement, on a bel et bien
          investi, mais ici», sourit Lucia. À les entendre, le passage des
          années n’a pas terni l’aura de leur pays d’accueil, même après une
          pandémie qui les a envoyés au front comme tout le personnel soignant.
        </p>
        <p>
          «Nos collègues suisses nous reprochent parfois de ne pas être plus
          engagés pour défendre la profession. Mais c’est difficile pour nous de
          critiquer les conditions actuelles dans le domaine des soins
          infirmiers. On sait trop bien ce qu’on a quitté.» Pour Lucia et Nuno,
          les choses n’ont malheureusement pas changé au Portugal après toutes
          ces années. «Pour nous faire venir en Suisse, c’est vrai qu’on nous a
          fait miroiter des choses qui ne sont pas vraiment arrivées, notamment
          au niveau de l’évolution salariale. Le coût de la vie s’est aussi
          révélé bien plus élevé. Mais la différence avec notre vie d’avant
          reste énorme et nous avons préféré nous intégrer», explique Nuno.
        </p>
        <p>
          Pour le jeune couple, se faire une place en Suisse ne veut pas dire
          tourner le dos au Portugal, au contraire. À travers la musique
          traditionnelle, cette «Tuna Helvetica» qui n’accueille pas que des
          lusophones, ils mêlent partage et retour aux sources. «C’est une
          manière de s’intégrer sans oublier d’où on vient, conclut Lucia. Il ne
          faut pas nous annuler, mais amener ce que nous avons de mieux.»
        </p>
      </>
    )
  }
]

export function AppComponent() {
  const [selectedPortraitIndex, setSelectedPortraitIndex] = useState(0)
  const [allowScroll, setAllowScroll] = useState(true)

  const handleReadMore = i => {
    if (!isMobile) {
      setSelectedPortraitIndex(i - 1)
      setAllowScroll(!allowScroll)
    }
  }

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Un canton de Vaud plus portugais qu’il ne l’imagine</h1>
            <h2>
              Depuis des années, notre plus grande communauté étrangère a ses
              racines au Portugal. En toute discrétion, ses enfants vivent leur
              vie en Vaudoises et Vaudois.
            </h2>
          </div>
        </div>
      </header>

      <div
        className='ui very fitted vertical segment'
        style={{ marginTop: '2em' }}
      >
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Chloé Din</div>
            <div className='ta updated'>Mis à jour le 18 septembre 2021</div>
          </div>
          <div className='ta text'>
            <p>
              «C’est à Avenches qu’on trouve le plus de Portugais dans le
              canton? J’aurais parié sur Renens.» Quand nous le contactons pour
              une rencontre, Mario Dantas s’amuse des statistiques que nous lui
              détaillons. Il faut dire qu’elles réservent quelques surprises.
            </p>
            <p>
              De toutes les communes vaudoises, l’ancienne cité romaine est
              celle où les Portugais sont le plus représentés. Avec 877
              personnes sur 4482 habitants, ils comptent pour 19,7% de la
              population totale, sans compter toutes celles et ceux qui ont
              aussi le passeport suisse.
            </p>
            <p>
              À l’échelle du canton aussi, la communauté étrangère portugaise
              est celle qui a le plus de poids: 6,7% de la population totale.
              Mais les Vaudois, et les Portugais parmi eux, en ont-ils
              conscience?
            </p>
            <p>
              Dans les années 80, Mario Dantas a quitté le Portugal en quête
              d’un autre avenir et a posé ses valises à Avenches. Bien d’autres
              coins de pays – parfois inattendus – ont attiré ses compatriotes.
              Comme le petit village de Bière, qui tient la deuxième place en
              termes de présence portugaise, avec 17,7% de la population, ou 288
              habitants.
            </p>
            <p>
              On est loin de la communauté lusitanienne de Renens, forte de 2757
              personnes, pour 13,2% de la population «seulement». À Lausanne,
              8210 Portugaises et Portugais représentent un «petit» 5,8% des
              Lausannois, moins que la moyenne cantonale. Derrière ces
              statistiques se cache une communauté qui brille souvent par sa
              discrétion, si bien que le canton de Vaud est sans doute plus
              portugais qu’il ne l’imagine. C’est un peu cela que souhaite vous
              faire découvrir «24 heures». Par petites touches, nous l’avons
              d’ailleurs déjà{' '}
              <a
                href='https://www.24heures.ch/tags/32678/portugal'
                target='_blank'
                rel='noreferrer'
              >
                un peu fait ces derniers mois
              </a>
              . Quand nous avons raconté{' '}
              <a
                target='_blank'
                href='https://www.24heures.ch/rencontre-avec-le-laureat-portugais-du-prix-de-lausanne-363424381163'
                rel='noreferrer'
              >
                ce danseur
              </a>
              , Grand Prix de la Ville de Lausanne, devenu star au pays, ou{' '}
              <a
                href='https://www.24heures.ch/hier-dans-un-car-pour-57-personnes-il-ny-en-avait-que-huit-318334370783'
                target='_blank'
                rel='noreferrer'
              >
                ce cariste
              </a>{' '}
              qui, à cause du Covid, faisait des allers-retours entre la Suisse
              et son pays quasi sans personne à son bord.
            </p>
          </div>
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <AutofitIFrame
            loading='lazy'
            aria-label='Interactive line chart'
            src='https://datawrapper.dwcdn.net/KXAbk/13'
          />
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <AutofitIFrame
            loading='lazy'
            aria-label='Interactive line chart'
            src='https://datawrapper.dwcdn.net/Rfioh/7'
          />
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <p>
              Dans le grand format que nous publions aujourd’hui, nous
              découvrons quelques-uns de ces Vaudois et Vaudoises dont le cœur
              est en partie au Portugal: la famille da Silva Pinto, qui a plongé
              ses racines à Bière, le Renanais Luis Guedes, récemment élu au
              Conseil communal, ou encore Nuno et Lucia Simoes, venus de
              Lisbonne pour travailler dans nos hôpitaux.
            </p>
            <p>
              Mario Dantas, lui, est l’un des fondateurs du centre portugais
              d’Avenches, parmi les plus importants de Suisse à sa grande
              époque. En égrenant ses souvenirs, il ne résiste pas au plaisir
              d’une virée en voiture dans sa commune d’adoption. Depuis le siège
              passager, on lui fait remarquer que la forte présence portugaise
              est loin de s’étaler sur les devantures des magasins ou des
              restaurants. Fondus dans la masse, les enfants du Portugal? Rien
              n’est moins sûr. «C’est une communauté complexe», glisse le
              quinquagénaire.
            </p>
          </div>
        </div>
      </div>

      {/* <VaccineStage /> */}
      {isMobile ? (
        <PinnedScreenMobile handleReadMore={handleReadMore} />
      ) : (
        <PinnedScreen
          allowScroll={allowScroll}
          portraitIndex={selectedPortraitIndex}
          handleReadMore={handleReadMore}
        />
      )}

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4>Les Français en passe de supplanter les Portugais</h4>
            <p>
              De toutes les communautés étrangères du canton, la portugaise est
              celle qui a connu l’essor le plus fulgurant en quarante ans. En
              1980, les statistiques dénombraient à peine plus de 2000 Portugais
              et Portugaises en terres vaudoises. Fin juin 2021, ce chiffre
              s’est hissé à 55’155 résidents permanents. Cela ne représente pas
              moins de 6,7% des Vaudois et un peu plus de 20% de la population
              étrangère du canton. Et c’est compter sans les près de 9000
              personnes qui détiennent aussi le passeport suisse.
            </p>
            <p>
              Si le Portugal est la nationalité étrangère la plus représentée
              dans le canton aujourd’hui, c’est en fait le cas depuis de longues
              années. En 2003 déjà, elle prenait le dessus sur l’Italie, alors
              première au classement. Désormais, elle distancie la France,
              devenue entre-temps deuxième communauté étrangère. Mais cette
              première place ne tient plus qu’à un cheveu. À fin juin, la
              différence n’est plus que de 1000 personnes et en chute libre
              depuis des mois.
            </p>
          </div>
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <AutofitIFrame
              loading='lazy'
              aria-label='Interactive line chart'
              src='https://datawrapper.dwcdn.net/NYB4b'
            />
          </div>
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4>Une vague de retours</h4>
            <p>
              Depuis 2017, le nombre de Portugais s’est mis à baisser dans le
              canton de Vaud, comme dans le reste de la Suisse. Du jamais vu,
              après des décennies marquées par deux vagues de migration, la
              première dans les années 80 et la deuxième, dans les années 2000.
              Le phénomène intrigue des chercheurs comme Philippe Wanner,
              professeur à l’Institut de démographie et de socioéconomie de
              l’Université de Genève, qui participe à une étude en cours sur
              l’aspiration au retour des immigrants venus du Portugal.
            </p>
            <p>
              Pour expliquer cette hausse récente de départs, le démographe cite
              notamment l’introduction de la transmission automatique des
              données bancaires. «Certaines personnes ont pu craindre de voir
              taxés leurs biens acquis au Portugal.» Mais les facteurs
              personnels comptent aussi pour beaucoup, par exemple le moment où
              les enfants achèvent leur formation. «Ces dernières années, deux
              classes d’âge ont été particulièrement représentées parmi les
              Portugais faisant leur retour au pays: 60 ans, qui correspondent à
              l’âge de la retraite dans le secteur de la construction, et 65
              ans.»
            </p>

            <h4>Une nostalgie tenace</h4>
            <p>
              La génération qui repart actuellement est celle de la première
              vague d’immigration portugaise des années 80. Mais elle n’est pas
              la seule à avoir les yeux tournés vers la mère patrie. «C’est une
              constante par rapport aux immigrants d’autres nationalités. Les
              Portugais se démarquent par leur volonté de retour. Le moment du
              départ dépend des circonstances, mais l’idée reste présente chez
              beaucoup d’entre eux», commente le démographe Philippe Wanner.
            </p>
            <p>
              L’enquête «Migration-mobility» qu’il a effectuée auprès de
              personnes arrivées en Suisse ces 12 dernières années montre en
              effet que le temps n’y fait rien: alors que chez les immigrés
              d’autres pays de l’Union européenne, le souhait de passer le reste
              de sa vie en Suisse augmente nettement avec les années (à 48,8%),
              ce sentiment reste assez stable – et faible – chez les Portugais
              (17,7%).
            </p>
            <p>
              Dans le canton de Vaud, cet attachement au pays d’origine peut
              aussi se lire dans le taux de naturalisation des Portugais. Avec
              1,02%, soit 576 passeports suisses attribués en 2020, ce taux est
              l’un des plus faibles, toutes nationalités confondues. C’est
              d’ailleurs le cas depuis de nombreuses années, malgré une poussée
              inédite de naturalisations, entre 2015 et 2019.
            </p>
          </div>
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <AutofitIFrame
              loading='lazy'
              aria-label='Interactive line chart'
              src='https://datawrapper.dwcdn.net/c1qLv'
            />
          </div>
        </div>
      </div>

      <div className='ui very fitted vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4>Des travailleurs invisibles</h4>
            <p>
              «Sur le nombre de Portugais établis en Suisse, la majorité est
              composée de personnes qui ne restent que quelques années avant de
              repartir, sans avoir le temps de s’intégrer», observe le
              démographe Philippe Wanner. Nombreux dans les statistiques, ces
              travailleurs restent ainsi à la limite de l’invisibilité dans
              notre société. Pourquoi?
            </p>
            <p>
              Un relevé effectué entre 2017 et 2018 sur les personnes nées au
              Portugal et installées en Suisse montre que 50% d’entre elles
              travaillaient dans l’industrie ou les métiers manuels ou non
              qualifiés. À l’inverse, seules 3,2% d’entre elles occupaient une
              fonction managériale. Les recherches récentes montrent aussi que
              l’immigration portugaise en Suisse reste peu qualifiée, un constat
              qui tranche avec l’évolution qu’ont connu les travailleurs
              d’Espagne et d’Italie notamment.
            </p>
            <p>
              Selon un autre sondage effectué en 2016, les Portugais étaient 24%
              à avoir une formation supérieure, contre 55% chez les Italiens et
              63% chez les Espagnols. «Il n’y a pas d’explication rationnelle,
              mais quelques hypothèses, avance Philippe Wanner. Par exemple,
              dans des domaines comme la construction, les réseaux portugais
              d’amis et de cousins installés en Suisse restent importants et
              offrent des opportunités à des personnes qui n’en trouvent pas au
              Portugal.»
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui container'>
          <h4 className='ui header'>Plus d'articles sur ce thème</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={['371566653563', '291559892953', '540739403636']}
          />
        </div>
      </div>

      <Footer
        hidePortfolioTeaser
        locale='fr'
        mailTo='interactif.tamedia.ch'
        articleId={ARTICLE_ID}
        credits={[
          ['Textes', 'Chloé Din'],
          ['Infographies', 'Océane Haenni'],
          ['Photos', 'Jean-Paul Guinnard, Christian Brun, Florian Cella']
        ]}
      />
    </div>
  )
}

// interface PortraitProps {
//   name: String
//   desc: String
//   quote: String
//   intro: String
// }

const Portrait = ({
  name,
  desc,
  quote,
  intro,
  text,
  pict,
  num,
  offset,
  handleReadMore
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [{ scale }, api] = useSpring(() => ({ scale: 0 }))
  const block = useRef(null)
  const textContainer = useRef(null)

  useEffect(() => {
    !isMobile &&
      api.start({
        scale: Math.max(
          0.65,
          Math.min(
            Math.abs(
              1 -
                (parseInt(offset.replace('px', '')) +
                  window.innerWidth * (num - 1)) /
                  window.innerWidth
            ),
            1
          )
        )
      })
  }, [offset])

  return (
    <div className={`temoignage-inner ${isOpen ? 'open' : ''}`} ref={block}>
      <div className='image-container'>
        <animated.div
          style={{
            backgroundImage: `url(${require(`../../images/${pict}`)})`,
            scale
          }}
          className='image-wrapper'
        />
      </div>
      {/* <animated.div>{translateY.to(v => v)}</animated.div> */}
      <div className='text-container' ref={textContainer}>
        <div className='text-wrapper'>
          <div className='number'>{num}</div>
          {isMobile && (
            <div className='quote-name'>
              {name}
              {/* - {desc} */}
            </div>
          )}
          <div className='quote'>
            {quote}
            {!isMobile && (
              <div className='quote-name'>
                {name}
                {/* - {desc} */}
              </div>
            )}
          </div>
          <div className='portrait-text'>
            {intro}
            {isOpen ? text : <div className='mask' />}
          </div>
          {isMobile && isOpen ? null : (
            <div
              className='button outline'
              onClick={() => {
                setIsOpen(!isOpen)
                handleReadMore(num)
                textContainer.current.scrollTo(0, 0)
                !isMobile &&
                  window.scrollTo(
                    0,
                    document
                      .getElementById('pinned-ghost')
                      .getBoundingClientRect().top + window.scrollY
                  )
              }}
            >
              {isOpen
                ? 'Revenir'
                : isMobile
                ? 'Lire le portrait'
                : 'Continuer la lecture'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PortraitMobile = ({
  name,
  desc,
  quote,
  intro,
  text,
  pict,
  num,
  offset,
  handleReadMore
}) => {
  const [isSticky, setIsSticky] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const block = useRef(null)
  const textContainer = useRef(null)
  const issti = useRef(false)

  const [{ opacity }, api] = useSpring(() => ({
    opacity: 0
  }))

  const [{ translateY }, apiTranslate] = useSpring(() => ({
    translateY: '-200px'
  }))

  const handleScroll = v => {
    const topPct = Math.min(
      Math.max(
        0,
        window.scrollY -
          block.current.getBoundingClientRect().top -
          window.scrollY
      ) / block.current.getBoundingClientRect().height,
      1
    )
    const viewPct = Math.min(
      Math.max(
        0,
        window.scrollY -
          block.current.getBoundingClientRect().top -
          window.scrollY +
          window.innerHeight
      ) / block.current.getBoundingClientRect().height,
      1
    )
    // console.log(num, topPct, viewPct)
    // console.log(num, block.current.getBoundingClientRect().top)
    const op =
      block.current.getBoundingClientRect().top >= 0 ? viewPct : 1 - topPct
    num === 1 && console.log(op)

    if (op === 1 && !issti.current) {
      setIsSticky(true)
      issti.current = true
    } else if (op < 1 && issti.current) {
      setIsSticky(false)
      issti.current = false
    }

    api.start({
      opacity: op
    })
    apiTranslate.start({
      translateY: -200 + op * 200 + 'px'
    })
  }

  useEffect(() => {
    if (isOpen) {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isOpen])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div className={`temoignage-wrapper-mobile ${isOpen ? 'open' : ''}`}>
      <animated.div
        className={`temoignage-inner mobile ${isOpen ? 'open' : ''} ${
          isSticky ? 'sticky' : ''
        }`}
        ref={block}
        style={{ opacity }}
      >
        <div className='image-container'>
          <animated.div
            style={{
              backgroundImage: `url(${require(`../../images/${pict}`)})`,
              translateY
            }}
            className='image-wrapper'
          />
        </div>
        {/* <animated.div>{translateY.to(v => v)}</animated.div> */}
        <div className='text-container' ref={textContainer}>
          <div className='text-wrapper'>
            <div className='number'>{num}</div>
            <div className='quote'>
              {quote}
              <div className='quote-name'>
                {name}
                {/* - {desc} */}
              </div>
            </div>
            <div className='portrait-text'>
              {intro}
              {isOpen ? text : <div className='mask' />}
            </div>
            {isMobile && isOpen ? null : (
              <div
                className='button outline'
                onClick={() => {
                  setIsOpen(!isOpen)
                  handleReadMore(num)
                  textContainer.current.scrollTo(0, 0)
                  !isMobile &&
                    window.scrollTo(
                      0,
                      document
                        .getElementById('pinned-ghost')
                        .getBoundingClientRect().top + window.scrollY
                    )
                }}
              >
                {isOpen
                  ? 'Revenir'
                  : isMobile
                  ? 'Lire le portrait'
                  : 'Continuer la lecture'}
              </div>
            )}
          </div>
        </div>
      </animated.div>
    </div>
  )
}

const AnimatedPortrait = animated(Portrait)

const PinnedScreen = ({ portraitIndex, handleReadMore, allowScroll }) => {
  // const mouse = useRef([0, 0])
  const [isPinned, setIsPinned] = useState(false)
  const [selectedPortrait, setSelectedPortrait] = useState(0)
  const block = useRef(null)
  const [{ translateX }, api] = useSpring(() => ({
    translateX: '0px'
  }))
  const p = useRef(0)

  const handleScroll = (vy, inverse) => {
    if (!allowScroll) {
      return 0
    }

    if (vy === 0) {
      // p.current = 0
    } else if (isPinned) {
      p.current -= vy * 20 * (inverse ? -1 : 1)
    }

    // console.log('p:', p.current)

    // if (Math.abs(p.current) > 35 && allowChangePortrait) {
    //   const newPortraitId =
    //     selectedPortrait + -p.current / Math.abs(p.current)
    //   setSelectedPortrait(
    //     newPortraitId < 0
    //       ? 0
    //       : newPortraitId > portraits.length - 1
    //       ? portraits.length - 1
    //       : newPortraitId
    //   )
    //   setAllowChangePortrait(false)
    //   setTimeout(() => {
    //     setAllowChangePortrait(true)
    //   }, 3000)
    // }

    // if (selectedPortrait === 3 && p.current < -10 && allowChangePortrait) {

    if (isPinned) {
      if (inverse) {
        if (vy > 5) {
          console.log('INVERSE')
          if (p.current < 0) {
            setIsPinned(false)
            p.current = 0
          }

          if (p.current > window.innerWidth * 3.25) {
            setIsPinned(false)
            p.current = window.innerWidth * 3.25
          }
        }
      } else {
        if (p.current > 0) {
          setIsPinned(false)
          p.current = 0
        }

        if (p.current < -window.innerWidth * 3.25) {
          setIsPinned(false)
          p.current = -window.innerWidth * 3.25
        }
      }
    }

    //   if (isPinned && p.current > 0) {
    //   setIsPinned(false)
    //   p.current = 0
    // }

    // if (isPinned && p.current < -window.innerWidth * 3.25) {
    //   setIsPinned(false)
    //   p.current = -window.innerWidth * 3.25
    // }

    // if (selectedPortrait === 0 && p.current > 10 && allowChangePortrait) {
    //   setIsPinned(false)
    // }

    api.start({
      translateX: p.current - selectedPortrait * window.innerWidth + 'px'
    })
  }

  const bind = useGesture({
    onDrag: ({ offset: [, y], vxvy: [, vy] }) => {
      !isMobile && handleScroll(vy, true)
    },
    onWheel: ({ offset: [, y], vxvy: [, vy] }) => {
      !isMobile && handleScroll(vy, false)
    }
  })

  useEffect(() => {
    const temoignages = document.getElementById('pinned-ghost')

    if (isPinned) {
      console.log('PINNED!')
      console.log(temoignages.getBoundingClientRect().top + window.scrollY)
      document.body.classList.add('frozen')
      // Why???
      setTimeout(() => {
        window.scrollTo(
          0,
          temoignages.getBoundingClientRect().top + window.scrollY
        )
      }, 100)
    } else {
      console.log('UN-PINNED!')
      document.body.classList.remove('frozen')
    }
  }, [isPinned])

  useEffect(() => {
    console.log(selectedPortrait)
    // p.current = 0
  }, [selectedPortrait])

  useEffect(() => {
    p.current = portraitIndex * -window.innerWidth
    api.start({
      translateX: p.current - selectedPortrait * window.innerWidth + 'px'
    })
    // p.current = 0
  }, [portraitIndex])

  // useEffect(() => {
  //   window.addEventListener('mousemove', e => {
  //     mouse.current = [
  //       e.clientX - window.innerWidth / 2,
  //       e.clientY - window.innerHeight / 2
  //     ]
  //   })
  // }, [])

  return (
    <>
      <div id='pinned-ghost' className={`ghost ${isPinned ? 'pinned' : ''}`} />
      <div
        id='temoignages'
        className={`temoignage-block ${isPinned ? 'pinned' : ''}`}
        ref={block}
        {...bind()}
      >
        {isPinned && (
          <div className='pager-container'>
            <animated.div
              className='pager'
              style={{
                width: translateX.to(
                  v =>
                    (Math.abs(parseInt(v.replace('px', ''))) /
                      (window.innerWidth * 3.25)) *
                      100 +
                    '%'
                )
              }}
            />
          </div>
        )}
        <Waypoint
          bottomOffset='90%'
          onEnter={() => {
            !isMobile && setIsPinned(true)
          }}
        />
        <div className='temoignage-content'>
          {portraits.map((p, i) => (
            <animated.div
              className='temoignage-wrapper'
              style={{ translateX }}
              key={`portrait-${i}`}
            >
              <AnimatedPortrait
                {...p}
                num={i + 1}
                offset={translateX.to(v => v)}
                handleReadMore={v => {
                  p.current = (v - 1) * -window.innerWidth
                  api.start({
                    translateX:
                      p.current - selectedPortrait * window.innerWidth + 'px'
                  })
                  handleReadMore(v)
                }}
              />
            </animated.div>
          ))}
        </div>
      </div>
    </>
  )
}

const PinnedScreenMobile = ({ handleReadMore }) => {
  return (
    <>
      <div id='temoignages' className='temoignage-block mobile'>
        <div className='temoignage-content'>
          {portraits.map((p, i) => (
            <div key={`portrait-${i}`}>
              <PortraitMobile
                {...p}
                num={i + 1}
                handleReadMore={handleReadMore}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
